import React from "react";
import { ReactComponent as ArrowRight } from "@assets/Icons/IconArrowRight.svg";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";

/**
 * The frame every block of the Overview action center sits in: a heading, an
 * optional one-line subtitle, an optional action on the right and the body.
 *
 * Not `GenericCard`: that one is built around a collapsible header and a radio
 * group, and the overview needs neither — only a quiet, uniform frame so the
 * page reads as one surface in both themes.
 */
export function OverviewPanel({
  title,
  subtitle,
  action,
  footer,
  className = "",
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`flex min-w-0 flex-1 flex-col rounded-lg border border-semantic-border-primary bg-semantic-bg-primary ${className}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 px-5 pb-3 pt-4">
        <div className="min-w-0">
          <h2 className="font-heading text-base font-bold leading-tight text-semantic-text-primary">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-0.5 text-xs text-semantic-text-secondary">{subtitle}</p>
          )}
        </div>
        {action}
      </header>
      <div className="flex min-h-0 flex-1 flex-col px-5 pb-5">{children}</div>
      {footer && (
        <footer className="border-t border-semantic-border-primary">{footer}</footer>
      )}
    </section>
  );
}

/**
 * The quiet "View all" link in a panel header. `href` is where it goes, so the
 * views gestures (right click "Add view", Cmd/Ctrl+click) work on it too.
 */
export function PanelLink({ label, href, onClick }: { label: string; href: string; onClick: () => void }) {
  return (
    <button
      type="button"
      {...viewHref(href)}
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-semantic-text-brand hover:underline"
    >
      {label}
      <ArrowRight className="h-3 w-3" aria-hidden />
    </button>
  );
}

/** Thin horizontal bar: `value` out of `max`, never past full. */
export function MeterBar({
  value,
  max,
  fillClassName = "bg-semantic-bg-brand-bold",
  heightClassName = "h-1.5",
}: {
  value: number;
  max: number;
  fillClassName?: string;
  heightClassName?: string;
}) {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className={`w-full overflow-hidden rounded-full bg-semantic-bg-tertiary ${heightClassName}`}>
      <div
        className={`h-full rounded-full ${fillClassName}`}
        // Inline: a computed `w-[NN%]` class would be invisible to the Tailwind scanner.
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}
